/**
 * 접속 기기가 PC 냐 휴대폰이냐 — **온 사이트가 이 한 곳을 본다**(2026-09-17 사용자 지적).
 *
 * 그동안 판정이 두 벌이었다. 서버(`apps/api` 의 `isMobileAgent`)는 로그인 세션의 기기 칸을 정하고,
 * 화면(`player-shared.ts` 의 `isMobileUa`)은 재생 화면을 갈랐다. 둘은 UA 글자만 보는데 **UA 로는 못 가리는
 * 기기가 있다** — 그래서 같은 기기가 한쪽에서는 모바일, 다른 쪽에서는 PC 로 읽혀 두 가지 일이 함께 났다.
 *  · 아이패드로 들어와도 PC 플레이어가 떴다
 *  · 휴대폰과 PC 로 따로 로그인했는데 **동시접속 차단**이 걸렸다(같은 기기 종류로 묶여 서로 끊었다)
 *
 * **왜 UA 로 못 가리나** — iPadOS 13 부터 사파리가 기본으로 데스크톱 UA(`Macintosh; Intel Mac OS X`)를 보낸다.
 * 맥과 글자가 한 자도 다르지 않아 서버에서는 가를 길이 없다. 어떤 UA 파서(ua-parser-js 등)를 가져와도 마찬가지다 —
 * 라이브러리가 부족해서가 아니라 **글자에 단서가 없다**. 휴대폰의 "데스크톱 사이트 요청" 도 같은 처지다.
 *
 * 그래서 **브라우저가 판단해 서버에 알려 준다**. 브라우저에는 UA 에 없는 단서가 있다 —
 * 맥은 터치 지점이 0 이고 아이패드는 5 다. 업계가 쓰는 방법이 이것이다.
 *  · 재생: `openPlayer()` 가 주소에 `m=1|0` 을 붙이고 재생 페이지가 그 값을 먼저 본다
 *  · 로그인: 폼이 `x-device` 헤더를 붙이고 BFF 가 API 로 이어 준다
 * 값이 없는 요청(주소를 직접 친 경우 등)은 UA 판정으로 되돌아간다 — 틀릴 수 있어도 없는 것보다 낫다.
 */

/** 브라우저가 스스로 낸 판정을 서버로 나르는 헤더 — 값은 `mobile` 또는 `pc` */
export const DEVICE_HEADER = 'x-device';

/**
 * UA 글자만 보는 판정 — **알려 준 값이 없을 때의 차선책**이다.
 * 낱말은 API 의 `core/auth/user-agent.ts` 와 같게 맞춘다(두 곳이 다르면 또 어긋난다).
 */
const MOBILE_RE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;
export const isMobileUa = (ua: string | null | undefined): boolean => !!ua && MOBILE_RE.test(ua);

/**
 * 이 브라우저가 모바일인가 — **브라우저에서만 부른다**(서버에서 부르면 늘 false).
 *  ① UA 에 모바일 낱말이 있으면 모바일
 *  ② `Macintosh` 인데 터치 지점이 여럿이면 아이패드다(맥은 0 이다)
 *  ③ 손가락으로만 쓰는 기기(`pointer: coarse` 이고 정밀 입력이 없다)면 화면이 넓어도 모바일 — 안드로이드 태블릿이 여기 걸린다
 *  ④ 마우스도 있는 터치 기기(터치 노트북)는 화면 폭으로 가른다
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (isMobileUa(ua)) return true;
  if (/Macintosh|Mac OS X/.test(ua) && navigator.maxTouchPoints > 1) return true;
  const mq = (q: string) => (typeof window.matchMedia === 'function' ? window.matchMedia(q).matches : false);
  const coarse = mq('(pointer: coarse)'); // 주 입력이 손가락인가
  /*
   * **마우스가 아예 없으면 화면이 넓어도 휴대기기다**(2026-09-17 사용자 제보 "안드로이드 태블릿도 PC 로 인식한다").
   * 태블릿은 화면이 1280 을 넘기도 해서 폭으로만 가르면 PC 로 떨어진다. 데스크톱 모드를 켜면 UA 에서 `Android` 도 사라진다.
   * `any-pointer: fine` 은 **어떤 입력장치든 정밀한 것이 하나라도 있는가**를 묻는다 — 폰·태블릿은 거짓, 마우스가 붙으면 참이다.
   */
  if (coarse && !mq('(any-pointer: fine)')) return true;
  // 마우스도 있는 터치 기기(터치 노트북)는 폭으로 가른다 — 좁으면 휴대기기로 본다
  return coarse && window.screen.width <= 1024;
}

/** 요청에 붙일 판정 — `fetch` 의 헤더에 펼쳐 넣는다 */
export const deviceHeader = (): Record<string, string> => ({ [DEVICE_HEADER]: isMobileDevice() ? 'mobile' : 'pc' });
